import {
    Buildings, components, GSAPAnimationMixer,
    isDesktop,
    isMobile, isNoMenu,
    projectMode,
    settingsData,
    threeJsVariables, TRoot,
    UIRoot
} from '../../settings';
import {ViewMode} from '../../common';
import {
    execGlobalThrottle,
    findSelectionAndCrmData,
    flatClick, getCylinderModeControlsType, isCylinderDisplayModeEqual, isOneMesh, lockScreenOrientation,
    removeSelectionBoxesLockColor,
    userPrevent,
} from '../../functions';
import {closePoiInfoWindowCardInOrbit, dealsToggle, poiIntersection, poiToggle} from '../poi/poiCss';
import {getHighlights} from '../threejs/autoSelectionBoxes';
import {autoZoomIsActive, fitModelInViewport, getZoomParams} from '../controls/zoomControl';
import { Vector3, Box3 } from 'three';
import {
    autoRotateTimerAction,
    getOrbitControlsDistanceAndPan,
    getPolarAngleFromViewMode,
    getWorldData, hideOrbitToMapButton,
    resetPanLimits,
    setMinAndMaxControlsDistance,
    setOrbitControls,
    setPanLimits,
    setPolarAngle,
} from '../controls/orbitControls';
import {animateCamera} from '../threejs/animation';
import TWEEN from '../../libs/tween.umd';
import {toggleStreetNames} from '../components/street-names/addStreetNames';
import {hideRightNavBar, moveElements, showRightNavBar} from '../menu/navigation/right-nav-bar';
import {
    addAsideContainerOnMouseHandlers,
    additionalMenuToggle,
    mobileBurgerStyleToggle,
    setMenuActive, setMenuElementsState,
    toggleMenuWidth
} from '../menu/menu';
import {
    closeUnitCard,
    hideBuildings, hideBuildingSwitcher,
    showBuildingSwitcher,
    showAllBuildings, enterToTheBuildingWithMaxAvailableApartments, showActiveBuilding, showOneBuildingMesh,
} from '../components/building-switch/building_switch';
import {
    cylinderFloorNumbers,
    removeCylinderFloorNumbers, rotateCylinderFloorNumbers
} from '../components/cylinderFloorNumbers/cylinderFloorNumbers';
import {buildingHoverOut} from '../threejs/raycast';
import {
    buildingsLabelHideWhenAnimationStart, buildingsLabelResize, hideBuildingLabels,
    showBuildingLabels,
} from '../components/building-switch/buildingLabels';
import {disposeTiles, environmentHide, environmentShowV2} from '../threejs/environment';
import {backgroundHide, backgroundShow} from '../threejs/sceneBackground';
import {
    changeSelectionBoxesStatusColor,
    markSelectedApartments,
    openSearchHandler,
    removeSearchContainer, scrollSearchListToActiveBuildingGroup
} from '../menu/search/search';
import {updateShadowMap} from '../threejs/lights';
import {
    applySafariFixForFlatLabels,
    removeSafariFixForFlatLabels,
    renderUpdateValuesOnDemand
} from '../../app';
import {hideTooltip} from '../components/flatTooltip/flatTooltip';
import {Amenities} from '../components/Amenities/Amenities';
import {analytics} from '../analytics/analytics';
import { hideObjectsInCylinder, hideSceneObjects, showSceneObjects } from '../points/points';
import {compassVisibleToggle} from '../components/compass/compass';
import {CylinderBase} from '../cylinderGrid/CylinderBase';
import {StaticClouds} from '../components/weather/StaticClouds';
import {BuildingOutline} from '../building/BuildingOutline';
import {DisplayOptions} from "../components/displayOptions/DisplayOptions";
import {PublicLayers} from '../components/publicLayers/PublicLayers';
import {OrbitDayAndNight} from '../liveAnimations/DayNight/OrbitDayAndNight';
import {Favorite} from '../components/Favorite/Favorite';
import {widgetsResize} from "../widgets/widgets";
import {Fullscreen} from '../components/Fullscreen/Fullscreen';
import {ScreenLogo} from "../components/ScreenLogo";
import {IntersectionLegenda} from "../components/IntersectionLegenda";
import {CylinderFlatLabels} from "../components/flatLabels/CylinderFlatLabels";
import {Environment} from '../components/Environment/Environment';
import gsap from 'gsap';
import {closeMultiCards} from "../cards/unitCards/unitCardFunctions";
import {Joystick} from '../controls/Joystick';
import {restoreFogParams, saveFogPreset, setFogParams} from '../post-processing/Passes/Fog';
import {MobileSearch} from "../menu/search/MobileSearch/MobileSearch";
import {EnvironmentLayer} from "../components/Environment/EnvironmentLayers/EnvironmentLayer";
import {CylinderBuildingLabels} from '../components/building-switch/CylinderBuildingLabels';
import {Objects} from '../threejs/Objects/Objects';
import { SalesPlane } from '../threejs/SalesPlane';
import { BmbyMode } from './BmbyMode';
import { Lerp } from '../projectTime/Lerp';
import { PointViewManager } from '../components/pointViewManager/PointViewManager';

export class Cylinder {
    constructor() {
        this.minAngle = getPolarAngleFromViewMode(ViewMode.Cylinder).minAngle;
        this.minDistance = null;
        this.maxDistance = null;
        this.selectionBoxes = null;
        this.switchBuildingDuration = 1000;
        this.cylinderEnterDuration = projectMode.LiteMode.isActive() ? 0.1 : 1500;
        this.cylinderBuildingLabels = new CylinderBuildingLabels();
    }

    async enter(building) {
        await threeJsVariables.cloth.removeCloth();

        if (config.features?.blockCylinderMode || userPrevent())
            return;

        if (building?.isMesh) {
            if (projectMode.LiteMode.isActive() && (projectMode.LiteMode.modeIs('picture') || projectMode.LiteMode.modeIs('empty'))) {
                const findBuilding = Buildings.getBuilding(building.name);
                if (!findBuilding)
                    enterToTheBuildingWithMaxAvailableApartments();
                return;
            } else {
                if (isDesktop && settingsData.isTouchEvent && !settingsData.doubleClick)
                    return;

                !projectMode.LiteMode.isActive() ?
                    building = Buildings.getBuilding(building.parent.name) :
                    building = Buildings.getBuilding(building.name.split('_')[0]);
                if (building && !Object.keys(building.info).length)
                    return;
            }
        }

        if (!building)
            return;
    
        settingsData.objects_to_intersection = [];

        return new Promise(async resolve => {
            // if (!config.features.dayNight.checked)
            //     await OrbitDayAndNight.turnOff();
    
            showOneBuildingMesh();
            projectMode.AirMode.toggleSceneObjects(true);
            threeJsVariables.orbitBuildingsNavigation.objectsVisibility(true);
            
            if (BmbyMode.active) {
                GSAPAnimationMixer.duration = 0.0001;
            }

            document.getElementById('app').classList.add('cylinder-mode');

            if (isMobile) {
                components.mobileSearch = new MobileSearch();
                ScreenLogo.hide();
                const pwa = document.querySelector('.pwa');
                pwa && pwa.classList.add('hidden');
            }

            document.querySelector('#NotiflixNotifyWrap:has( .sold-apartment-from__text)')?.classList.add('hidden');
            applySafariFixForFlatLabels();
            userPrevent(true);

            saveFogPreset();
            PublicLayers.hide();
            Environment.hide();
            hideOrbitToMapButton();
            StaticClouds.toggle('hide');

            const disclaimer = document.querySelector('#threejs > .disclaimer');
            if (isMobile && disclaimer)
                disclaimer.classList.add('hidden');


            BuildingOutline.toggle(false);
            DisplayOptions.hide();
            Fullscreen.visibilityToggle('hide');
            settingsData.activeModeSwitching = true;
            hideBuildingLabels();
            Buildings.activateBuilding(building.name);
            IntersectionLegenda.build();
            threeJsVariables.googleMiniMap.activateBuilding();
            threeJsVariables.schematicBuilding?.updateBuildingsRotationUI();
            projectMode.LiteMode.switchSelections();
            if (projectMode.LiteMode.isActive()) {
                projectMode.LiteMode.hideLiteMode();
                if (projectMode.LiteMode.modeIs('picture') || projectMode.LiteMode.modeIs('empty')) {
                    this.setApartmentsToIntersection();
                    userPrevent(false);
                    settingsData.activeViewMode = ViewMode.Cylinder;
                    widgetsResize();
                    projectMode.LiteMode.activeViewMode();
                    if (isMobile) {
                        components.mobileSearch.build();
                        new IntersectionLegenda(isDesktop ? document.querySelector('.aside__container') : TRoot);
                    } else {
                        await openSearchHandler()
                            .then(() => {
                                toggleMenuWidth({action: 'mini', source: 'eventListener'});
                                new IntersectionLegenda(isDesktop ? document.querySelector('.aside__container') : TRoot);
                                addAsideContainerOnMouseHandlers();
                                scrollSearchListToActiveBuildingGroup(building.name);

                                resolve(true);
                            });
                    }
                    showRightNavBar();
                    setMenuActive('search');
                    analytics('page_entrance', 'search');
                    projectMode.Pagination.paginate({source: 'menuHandler', note: 'cylinderMode', menuItem: 'search'});
                    settingsData.activeModeSwitching = false;
                    if (isNoMenu) {
                        document.querySelector('.aside__navigation-menu__item[data-menu-item="search"]')?.classList.add('hidden');
                        document.querySelector('.button-modes--back[data-button="back"]')?.classList.remove('hidden');
                    }

                    return;
                }
                compassVisibleToggle('show');
            }

            buildingHoverOut();
            autoRotateTimerAction('cancel');
            removeSelectionBoxesLockColor(undefined, true);

            isMobile ?
                closePoiInfoWindowCardInOrbit(true) :
                closePoiInfoWindowCardInOrbit(false);

            const currentBuilding = building.selectionsGroup;
            this.calculateMinDistance(currentBuilding);
            const buildingBox = new Box3().setFromObject(currentBuilding);
            await fitModelInViewport(false, currentBuilding);
            this.updateVariables();
            const buildingBoxCenter = buildingBox.getCenter(new Vector3());
            const buildingCenterPoint = currentBuilding.getWorldPosition(new Vector3()).setY(buildingBoxCenter.y);
            const {cameraVector, orbitControlsVector} = getWorldData();
            let length;
            this.selectionBoxes = currentBuilding.parent;

            if (autoZoomIsActive()) {
                const {foreshortening} = getZoomParams();
                length = foreshortening;
            }  else {
                length = (this.maxDistance + this.minDistance) / 2;
            }
            const position = new Vector3()
                .subVectors(cameraVector, buildingCenterPoint)
                .normalize()
                .setY(0)
                .setLength(length)
                .add(buildingCenterPoint);
            buildingCenterPoint.y = buildingCenterPoint.y / 1.2;
            settingsData.activeViewMode = ViewMode.Cylinder;

            // threeJsVariables.miniMap.hide();
            widgetsResize();
            additionalMenuToggle();

            const mixer = GSAPAnimationMixer.registerNewMixer('cylinderEnter');
            mixer.addCallback('onComplete', () => {
                threeJsVariables.scene.add(this.selectionBoxes);
                threeJsVariables.renderList.reorder();
                if (BmbyMode.active) {
                    GSAPAnimationMixer.duration = 1;
                }
                disposeTiles();
                if (config.features.privateHouseMode) {
                    setOrbitControls(ViewMode.Orbit);
                    setPolarAngle(ViewMode.Orbit);
                    setMinAndMaxControlsDistance(ViewMode.Orbit);
                    setPanLimits(ViewMode.Orbit);
                } else if (isCylinderDisplayModeEqual('show all')) {
                    const controlsType = getCylinderModeControlsType();
                    setOrbitControls(controlsType);
                    setPolarAngle(controlsType);
                    setMinAndMaxControlsDistance(controlsType);
                    setPanLimits(controlsType);
                } else {
                    setOrbitControls(ViewMode.Cylinder);
                    setPolarAngle(ViewMode.Cylinder);
                    setMinAndMaxControlsDistance(ViewMode.Cylinder);
                    setPanLimits(ViewMode.Cylinder);
                }
                cylinderFloorNumbers().then(() => rotateCylinderFloorNumbers());
                this.cylinderBuildingLabels.create();
                if (isMobile) {
                    setMenuActive('search');
                    this.setApartmentsToIntersection(building);

                    if (config.features.privateHouseMode)
                        components.mobileSearch.build();
                } else {
                    openSearchHandler()
                        .then(() => {
                            setMenuActive('search');
                            toggleMenuWidth({action: 'mini', source: 'eventListener'});
                            new IntersectionLegenda(isDesktop ? document.querySelector('.aside__container') : TRoot);
                            // addFilterInteractionListeners();
                            this.setApartmentsToIntersection(building);
                            addAsideContainerOnMouseHandlers();
                            scrollSearchListToActiveBuildingGroup(building.name);

                            buildingsLabelHideWhenAnimationStart();

                            resolve(true);
                        });
                }

                if (!projectMode.LiteMode.isActive())
                    userPrevent(false);
            });

            if (threeJsVariables.sceneObjectsSlicer.active) {
                threeJsVariables.sceneObjectsSlicer.target = currentBuilding;
                mixer.addAnimation(threeJsVariables.sceneObjectsSlicer.cropXZ());
            }

            if (isOneMesh() && threeJsVariables.slicer.active) {
                threeJsVariables.slicer.target = currentBuilding;
                mixer.addAnimation(threeJsVariables.slicer.cropXZ());
            }
            
            projectMode.AirMode.cylinderEnterAnimation(mixer);
            
            const animation = animateCamera(
                cameraVector,
                config.features.privateHouseMode ? cameraVector : position,
                orbitControlsVector,
                buildingCenterPoint,
                undefined,
                this.minDistance,
                this.minAngle,
                config.threejs.orbitControls.polarCameraCorrection,
                this.cylinderEnterDuration,
                TWEEN.Easing.Sinusoidal.InOut,
                () => {
                    buildingsLabelHideWhenAnimationStart();
                    OrbitDayAndNight.presetsToggle('reset');
                    toggleStreetNames(false);
                    poiToggle(false);
                    resetPanLimits();
                },
            );
            mixer.addAnimation(animation);

            const cylinderPass = threeJsVariables.composer.getPass('cylinderPass');
            const fadeProgress = {
                value: 0
            };

            if (!config.features.privateHouseMode && !isCylinderDisplayModeEqual('show all')) {
                const fadeAnimation = gsap.to(fadeProgress, {
                    value: config.features.buildingOpacity === 0 ? 1 : config.features.buildingOpacity,
                    duration: GSAPAnimationMixer.duration,
                    ease: GSAPAnimationMixer.ease,
                    onStart: () => {
                        cylinderPass.active = true;
                        Objects.visibilityToggle(false);
                    },
                    onUpdate: () => cylinderPass.setProgress(fadeProgress.value),
                    onComplete: () => {
                        hideSceneObjects(building);
                        hideObjectsInCylinder(true);
                        OrbitDayAndNight.toggle('hide');
                        environmentHide();
                        showActiveBuilding();
                        hideBuildings();
                        buildingsLabelHideWhenAnimationStart();
                        toggleStreetNames(false);
                        setFogParams({
                            active: false
                        });
                        SalesPlane.scope?.hide();
                        cylinderPass.active = Boolean(config.features.buildingOpacity);

                        if (isMobile) {
                            components.mobileSearch.build();
                            new IntersectionLegenda(TRoot);
                        }
                    }
                });
                mixer.addAnimation(fadeAnimation);
            } else {
                const plugProgress = {
                    value: 0
                };
                const plugAnimation = gsap.to(plugProgress, {
                    value: 1,
                    duration: GSAPAnimationMixer.duration,
                    ease: GSAPAnimationMixer.ease,
                    onStart: () => Objects.visibilityToggle(false),
                    onComplete: () => {
                        hideSceneObjects(building);
                        hideObjectsInCylinder(true);
                        environmentHide();
                        showActiveBuilding();
                        hideBuildings();
                        toggleStreetNames(false);
                        setFogParams({
                            active: false
                        });
                    }

                });
                mixer.addAnimation(plugAnimation);
            }


            backgroundHide();
            EnvironmentLayer.resetToDefault();
            CylinderBase.show(building);
            PointViewManager.resetState();

            mixer.play();
            poiToggle(false);
            dealsToggle(false);
            toggleStreetNames(false);
            Amenities.createMainContainer();
            new Amenities(building).add();
            lockScreenOrientation();
            Favorite.toggle('show');
            new CylinderFlatLabels();
            analytics('page_entrance', 'search');
            projectMode.Pagination.paginate({source: 'menuHandler', note: 'cylinderMode', menuItem: 'search'});
            settingsData.activeModeSwitching = false;
            showRightNavBar();
            if (isNoMenu) {
                document.querySelector('.aside__navigation-menu__item[data-menu-item="search"]')?.classList.add('hidden');
                document.querySelector('.button-modes--back[data-button="back"]')?.classList.remove('hidden');
            }

            if (!isMobile)
                showBuildingSwitcher();
            Favorite.intersect();
            if (isDesktop)
                setMenuElementsState('hidden');
            mobileBurgerStyleToggle();

            if (projectMode.LiteMode.isActive() && (projectMode.LiteMode.modeIs('regular') || projectMode.LiteMode.modeIs('schematic') || projectMode.LiteMode.modeIs('rotate')))
                userPrevent(false);
        });
    }

    calculateMinDistance(selections) {
        if (isMobile) {
            const coefficient = config.threejs.orbitControls.cylinder.mobile.distanceCoefficient || 1;
            const box = new Box3();
            const size = new Vector3();
            box.setFromObject(selections);
            box.getSize(size);
            const maxSize = Math.max(size.x,size.z) * coefficient;
            config.threejs.orbitControls.cylinder.mobile.portrait.min = maxSize;
            config.threejs.orbitControls.cylinder.mobile.landscape.min = maxSize;
        }
    }

    async switchBuilding(name, dataEngineID, isPostMessageSwitch) {
        if (userPrevent() || settingsData.globalThrottle || projectMode.LiteMode.modeIs('picture'))
            return;

        return new Promise(async (resolve) => {
            execGlobalThrottle(750);
            userPrevent(true);
            removeSelectionBoxesLockColor(undefined, true);
            if (isDesktop)
                Amenities.unselectAmenities();
            const mixer = GSAPAnimationMixer.registerNewMixer('switchingBuilding');
            Favorite.toggle('hide');
            const oldBuilding = Buildings.getActiveBuilding();
            const building = Buildings.getBuilding(name);
            const cylinderPass = threeJsVariables.composer.getPass('cylinderPass');
            const fadeProgress = {
                value: config.features.buildingOpacity === 0 ? 1 : config.features.buildingOpacity
            };

            if (BmbyMode.active && !isPostMessageSwitch) {
                const buildingId = config.buildings.find(b => b.buildingEngineId === building.name)?.buildingId;
                if (buildingId) {
                    const message = {
                        action: 'SwitchBuilding',
                        data: {
                            buildingId
                        }
                    };
                    window.parent.postMessage(JSON.stringify(message),"*");
                }
            }

            if (!config.features.privateHouseMode && !isCylinderDisplayModeEqual('show all')) {
                const fadeAnimation = gsap.to(fadeProgress, {
                    value: config.features.buildingOpacity === 0 ? 0 : config.features.buildingOpacity,
                    duration: GSAPAnimationMixer.duration,
                    ease: GSAPAnimationMixer.ease,
                    onStart: () => {
                        closeMultiCards(true);
                        hideSceneObjects();
                        cylinderPass.active = true;
                        building.modelGroup.visible = true;
                        building.isHide = false;
                    },
                    onUpdate: () => {
                        cylinderPass.setProgress(fadeProgress.value);

                        if (fadeProgress.value <= 0.5) {
                            oldBuilding.modelGroup.visible = Boolean(config.features.buildingOpacity);
                        }

                },
                onComplete: () => {
                    Buildings.deactivateBuilding();
                    Buildings.activateBuilding(building.name);
                    threeJsVariables.schematicBuilding?.updateBuildingsRotationUI();
                    threeJsVariables.googleMiniMap.activateBuilding();
                    projectMode.LiteMode.switchSelections();
                    showActiveBuilding();
                    hideBuildings();
                    showSceneObjects(building);
                    IntersectionLegenda.build();
                    Amenities.show();
                    cylinderPass.active = Boolean(config.features.buildingOpacity);
                    scrollSearchListToActiveBuildingGroup(building.name);
                }
            });
            mixer.addAnimation(fadeAnimation);
        } else {
            const plugProgress = {
                value: 0
            };

            const plugAnimation = gsap.to(plugProgress,{
                value: 1,
                duration: GSAPAnimationMixer.duration,
                ease: GSAPAnimationMixer.ease,
                onStart: () => {
                    closeMultiCards(true);
                    hideSceneObjects();
                },
                onComplete: () => {
                    Buildings.deactivateBuilding();
                    Buildings.activateBuilding(building.name);
                    threeJsVariables.googleMiniMap.activateBuilding();
                    threeJsVariables.schematicBuilding?.updateBuildingsRotationUI();
                    projectMode.LiteMode.switchSelections();
                    showActiveBuilding();
                    hideBuildings();
                    showSceneObjects(building);
                    IntersectionLegenda.build();
                    Amenities.show();
                    // if (isMobile && !UIRoot.querySelector('.search__container-main'))
                    //     openSearchHandler();
                    scrollSearchListToActiveBuildingGroup(building.name);
                }
            });
            mixer.addAnimation(plugAnimation);
        }

        new Amenities(building).add();

        if (document.getElementById('building-switcher')) {
            document.querySelectorAll('#building-switcher .building-item.active').forEach(item => item.classList.remove('active'));
            document.querySelectorAll(`.building-item[data-building="${building.name}"]`).forEach(item => item.classList.add('active'));
        }

            const buildingSelectionBoxes = building.selectionsGroup;
            this.calculateMinDistance(buildingSelectionBoxes);
            const selectionBox = new Box3().setFromObject(buildingSelectionBoxes);
            await fitModelInViewport(false, buildingSelectionBoxes);
            this.updateVariables();
            const buildingBoxCenter = selectionBox.getCenter(new Vector3());
            const buildingCenterPoint = buildingSelectionBoxes.getWorldPosition(new Vector3()).setY(buildingBoxCenter.y);
            const {cameraVector, orbitControlsVector} = getWorldData();
            let length;

        if (autoZoomIsActive()) {
            const {foreshortening} = getZoomParams();
            length = foreshortening;
        } else {
            length = (this.maxDistance + this.minDistance) / 2;
        }

            const position = new Vector3()
                .subVectors(cameraVector, buildingCenterPoint)
                .normalize()
                .setLength(length)
                .add(buildingCenterPoint);
            buildingCenterPoint.y = buildingCenterPoint.y / 1.2;

            mixer.addCallback('onComplete', () => {
                cylinderFloorNumbers().then(() => rotateCylinderFloorNumbers());
                changeSelectionBoxesStatusColor(settingsData.searchResult, Buildings.getActiveBuilding());

                if (config.features.privateHouseMode) {
                    setPolarAngle(ViewMode.Orbit);
                    setMinAndMaxControlsDistance(ViewMode.Orbit);
                    setPanLimits(ViewMode.Orbit);
                } else if (isCylinderDisplayModeEqual('show all')) {
                    const controlsType = getCylinderModeControlsType();
                    setPolarAngle(controlsType);
                    setMinAndMaxControlsDistance(controlsType);
                    setPanLimits(controlsType);
                } else {
                    setPolarAngle(ViewMode.Cylinder);
                    setMinAndMaxControlsDistance(ViewMode.Cylinder);
                    setPanLimits(ViewMode.Cylinder);
                }

                this.setApartmentsToIntersection(building);
                userPrevent(false);
                if (!isMobile && UIRoot.querySelector('.search__result-item.active') && dataEngineID) {
                    markSelectedApartments(dataEngineID);
                    const {selectedMesh} = findSelectionAndCrmData(dataEngineID);
                    flatClick(selectedMesh);
                }

                resolve(true);
            });

            const cameraAnimation = animateCamera(
                cameraVector,
                config.features.privateHouseMode ? cameraVector : position,
                orbitControlsVector,
                buildingCenterPoint,
                undefined,
                this.minDistance,
                this.minAngle,
                config.threejs.orbitControls.polarCameraCorrection,
                this.switchBuildingDuration,
                TWEEN.Easing.Sinusoidal.InOut,
                () => {
                    buildingHoverOut();
                    resetPanLimits();
                    removeCylinderFloorNumbers();
                }
            );

            if (threeJsVariables.sceneObjectsSlicer.active) {
                threeJsVariables.sceneObjectsSlicer.target = building.selectionsGroup;
                const slicerAnimation = threeJsVariables.sceneObjectsSlicer.cropXZ(0.1);
                mixer.addAnimation(slicerAnimation);
            }

            if (isOneMesh() && threeJsVariables.slicer.active) {
                threeJsVariables.slicer.target = building.selectionsGroup;
                const slicerAnimation = threeJsVariables.slicer.cropXZ(0.1);
                mixer.addAnimation(slicerAnimation);
            }

            CylinderBase.hide();
            CylinderBase.show(building);

            mixer.addAnimation(cameraAnimation);
            mixer.play();

            Favorite.toggle('show');

            settingsData.activeModeSwitching = false;
            Favorite.intersect();
        });
    }

    async exitFromMode() {
        if (userPrevent())
            return;

        document.getElementById('app').classList.remove('cylinder-mode');
        removeSafariFixForFlatLabels();
        // removeFilterInteractionListeners();
        userPrevent(true);
        toggleMenuWidth({action: 'full', source: 'cylinder'});
        settingsData.activeModeSwitching = true;
        renderUpdateValuesOnDemand(false);
        hideRightNavBar();
        Amenities.hide();
        IntersectionLegenda.remove();
        const disclaimer = document.querySelector('#threejs > .disclaimer')
        if (isMobile) {
            disclaimer.classList.remove('hidden');
            const pwa = document.querySelector('.pwa');
            pwa && pwa.classList.remove('hidden');
        }

        CylinderFlatLabels.remove();
        hideBuildingSwitcher();
        Favorite.toggle('hide');
        if (isMobile) {
            // await removeSearchContainer('immediately');
            components.mobileSearch.remove();
            components.mobileSearch = null;
        } else {
            await removeSearchContainer('immediately');
        }
        closeUnitCard();
        removeSelectionBoxesLockColor(undefined, true);
        settingsData.activeViewMode = ViewMode.Orbit;
        UIRoot.querySelector('.aside__container')?.classList.remove('fixed')
        widgetsResize();
        // Buildings.deactivateBuilding();
        showBuildingLabels();
        if (isMobile)
            ScreenLogo.show();
        SalesPlane.scope?.show();

        let buildingSize = new Vector3();
        let sBoxSize = new Vector3();

        if (projectMode.LiteMode.isActive()) {
            if (projectMode.LiteMode.modeIs('picture') || projectMode.LiteMode.modeIs('empty')) {
                userPrevent(false);
                settingsData.objects_to_intersection = projectMode.LiteMode.pictureModeOrbitIntersections;
                settingsData.activeModeSwitching = false;
                projectMode.LiteMode.activeViewMode();
                projectMode.LiteMode.showLiteMode();
                setMenuActive('3dModel');
                DisplayOptions.show();
                if (isNoMenu) {
                    document.querySelector('.aside__navigation-menu__item[data-menu-item="search"]')?.classList.remove('hidden');
                    document.querySelector('.button-modes--back[data-button="back"]')?.classList.add('hidden');
                }

                return;
            }
            compassVisibleToggle('hide');
        } else if (projectMode.LiteMode.isActive() && projectMode.LiteMode.modeIs('schematic')) {
            const sBoxes = getHighlights();
            new Box3().setFromObject(sBoxes).getSize(sBoxSize);
            buildingSize.copy(sBoxSize);
        } else {
            const buildings = threeJsVariables.scene.getObjectByName('Project Buildings');
            new Box3().setFromObject(buildings).getSize(buildingSize);
            const sBoxes = getHighlights();
            new Box3().setFromObject(sBoxes).getSize(sBoxSize);
        }

        const totalSize = new Vector3(buildingSize.x, sBoxSize.y, buildingSize.z);
        await fitModelInViewport(false, totalSize);

        const {cameraVector, orbitControlsVector} = getWorldData();
        const target = config.threejs.orbitControls.target;
        const {minAngle} = getPolarAngleFromViewMode(ViewMode.Orbit);
        const {minDistance, maxDistance} = getOrbitControlsDistanceAndPan(ViewMode.Orbit);
        const foreshortening = config.threejs.orbitControls.autoZoomSettings.foreshortening;
        let length;

        autoZoomIsActive() ?
            length = foreshortening :
            length = (maxDistance + minDistance) / 2;

        let defaultTarget = new Vector3(target.x, target.y, target.z);
        let position = new Vector3().subVectors(cameraVector, orbitControlsVector).normalize().setY(0).setLength(length).add(orbitControlsVector);

        removeCylinderFloorNumbers();
        threeJsVariables.orbitBuildingsNavigation.updateActiveButton();

        if (config.threejs.orbitControls.returnToDefaultView === false) {
            defaultTarget.copy(orbitControlsVector);
            defaultTarget.setY(target.y);
            position.setY(target.y);
        }

        settingsData.customSelections = [];
        settingsData.objects_to_intersection = [];
        settingsData.instanced_floors = [];
        settingsData.floor_obj = {};

        const mixer = GSAPAnimationMixer.registerNewMixer('exitToOrbit');
        mixer.addCallback('onComplete', () => {
            Joystick.updatePosition();
            showBuildingLabels();
            updateShadowMap();
            // threeJsVariables.miniMap.show();
            userPrevent(false);
        });

        if (!projectMode.LiteMode.isActive() && !Environment.getActivePublicLayer()?.animateCamera && !PublicLayers.activeLayer?.animateCamera) {
            
            if (!projectMode.AirMode.active) {
                const cameraAnimation = animateCamera(
                    cameraVector,
                    config.features.privateHouseMode ?
                        cameraVector : position,
                    orbitControlsVector,
                    config.features.privateHouseMode ? orbitControlsVector : defaultTarget,
                    undefined,
                    config.features.privateHouseMode ? this.minDistance : length,
                    minAngle,
                    0,
                    1000,
                    TWEEN.Easing.Sinusoidal.InOut,
                    () => {
                        threeJsVariables.trackballControls.maxDistance = maxDistance;
                        setOrbitControls(ViewMode.Orbit);
                        setPolarAngle(ViewMode.Orbit);
                        setPanLimits(ViewMode.Orbit);
                        hideTooltip();
                        this.cylinderBuildingLabels.remove();
                    },
                    () => {
                        setMinAndMaxControlsDistance(ViewMode.Orbit);
                        hideTooltip();
                    },
                );
    
                mixer.addAnimation(cameraAnimation);
            }
        }

        if (threeJsVariables.sceneObjectsSlicer.active) {
            mixer.addAnimation(threeJsVariables.sceneObjectsSlicer.restoreXZ(0.5));
        }

        if (isOneMesh() && threeJsVariables.slicer.active) {
            mixer.addAnimation(threeJsVariables.slicer.restoreXZ(0.5));
        }
        
        projectMode.AirMode.cylinderExitAnimation(mixer);

        threeJsVariables.scene.remove(this.selectionBoxes);
        this.selectionBoxes = null;

        const cylinderPass = threeJsVariables.composer.getPass('cylinderPass');
        const fadeProgress = {
            value: config.features.buildingOpacity === 0 ? 1 : config.features.buildingOpacity
        };

        if (!config.features.privateHouseMode && !isCylinderDisplayModeEqual('show all')) {
            const fadeAnimation = gsap.to(fadeProgress, {
                value: 0,
                duration: GSAPAnimationMixer.duration,
                ease: GSAPAnimationMixer.ease,
                onStart: () => {
                    cylinderPass.active = true;
                    poiIntersection();
                    showAllBuildings();
                    showSceneObjects();
                    projectMode.AirMode.toggleSceneObjects(false);
                    threeJsVariables.orbitBuildingsNavigation.objectsVisibility(false);
                    hideObjectsInCylinder(false);
                    environmentShowV2();
                    CylinderBase.hide();
                    OrbitDayAndNight.toggle('show');
                    Objects.visibilityToggle(true);
                    restoreFogParams();
                    // backgroundShow();
                },
                onUpdate: () => {
                    cylinderPass.setProgress(fadeProgress.value);
                    // buildingsLabelResize();
                },
                onComplete: () => {
                    Buildings.deactivateBuilding();
                    if (projectMode.AirMode.active) {
                        hideBuildings();
                    }
                    threeJsVariables.googleMiniMap.activateBuilding();
                    threeJsVariables.schematicBuilding?.updateBuildingsRotationUI();
                    cylinderPass.active = false;
                    document.getElementById('app').classList.remove('cylinder-mode');
                    buildingsLabelResize();
                }
            });
            mixer.addAnimation(fadeAnimation);
        } else {
            const plugProgress = {
                value: 0
            };

            const plugAnimation = gsap.to(plugProgress, {
                value: 1,
                ease: GSAPAnimationMixer.ease,
                duration: GSAPAnimationMixer.duration,
                onStart: () => {
                    poiIntersection();
                    showAllBuildings();
                    showSceneObjects();
                    hideObjectsInCylinder(false);
                    environmentShowV2();
                    Objects.visibilityToggle(true);
                    CylinderBase.hide();
                    OrbitDayAndNight.toggle('show');
                    restoreFogParams();
                    // backgroundShow();
                },
                // onUpdate: () => {buildingsLabelResize()},
                onComplete: () => {
                    Buildings.deactivateBuilding();
                    threeJsVariables.googleMiniMap.activateBuilding();
                    threeJsVariables.schematicBuilding?.updateBuildingsRotationUI();
                    cylinderPass.active = false;
                    document.getElementById('app').classList.remove('cylinder-mode');
                    buildingsLabelResize();
                    if (projectMode.AirMode.active) {
                        hideBuildings();
                    }
                }
            });
            mixer.addAnimation(plugAnimation);
        }
        backgroundShow();

        projectMode.LiteMode.isActive() && projectMode.LiteMode.showLiteMode();
        PublicLayers.show();
        Environment.show();
        Environment.mouseOverHandler();
        BuildingOutline.toggle(true);
        DisplayOptions.show();
        Fullscreen.visibilityToggle('show');
        StaticClouds.toggle('show');
        OrbitDayAndNight.presetsToggle('restore');
        lockScreenOrientation();
        // threeJsVariables.miniMap.show();

        mixer.play();
        moveElements();

        if (isNoMenu) {
            document.querySelector('.aside__navigation-menu__item[data-menu-item="search"]')?.classList.remove('hidden');
            document.querySelector('.button-modes--back[data-button="back"]')?.classList.add('hidden');
        }

        setMenuActive('3dModel');
        additionalMenuToggle();
        autoRotateTimerAction('update');
        settingsData.activeModeSwitching = false;
        toggleStreetNames(true);
        poiToggle(true);
        dealsToggle(true);
        mobileBurgerStyleToggle();

        if (isDesktop)
            setMenuElementsState('visible');
    }

    updateVariables() {
        this.minDistance = getOrbitControlsDistanceAndPan(ViewMode.Cylinder).minDistance;
        this.maxDistance = getOrbitControlsDistanceAndPan(ViewMode.Cylinder).maxDistance;
    }

    setApartmentsToIntersection(building) {
        if (projectMode.LiteMode.isActive() && projectMode.LiteMode.modeIs('picture')) {
            const buildings = Buildings.getBuildingsArray();
            const apartments = [];

            buildings.forEach(building => apartments.push(...building.getApartments()));
            projectMode.LiteMode.pictureModeOrbitIntersections.forEach(mesh => mesh.material.opacity = 0);
            settingsData.objects_to_intersection = apartments;

            return;
        } else if (projectMode.LiteMode.isActive() && projectMode.LiteMode.modeIs('empty')) {
            return;
        }

        if (config.features.privateHouseMode) {
            const buildings = Buildings.getBuildingsArray();
            const objects_to_intersection = [];
            const customSelections = [];
            const instanced_floors = [];
            const floor_obj = [];
            buildings.forEach(building => {
                objects_to_intersection.push(...building.apartments);
                objects_to_intersection.push(...building.getFloorPlugMeshes());
                objects_to_intersection.push(...building.customSelections.filter(selection => selection?.isObject3D));
                customSelections.push(...building.customSelections);
                instanced_floors.push(...settingsData.instanced_floors_switch[building.name]);
                floor_obj.push(...building.selections);
            });
            objects_to_intersection.push(...settingsData.additionalIntersectionObjects);
            settingsData.objects_to_intersection = objects_to_intersection;
            settingsData.customSelections = customSelections;
            settingsData.instanced_floors = instanced_floors;
            settingsData.floor_obj = floor_obj;
        } else {
            settingsData.objects_to_intersection = building.apartments.concat(building.getFloorPlugMeshes());
            settingsData.customSelections = building.customSelections.filter(selection => selection?.isObject3D);
            settingsData.objects_to_intersection = settingsData.objects_to_intersection.concat(settingsData.customSelections);
            settingsData.objects_to_intersection = settingsData.objects_to_intersection.concat(settingsData.additionalIntersectionObjects);
            settingsData.instanced_floors = settingsData.instanced_floors_switch[building.name];
            settingsData.floor_obj = building.selections;
        }


        if (document.querySelector('.search-btn_tooltip'))
            settingsData.isSwitched = true;
    }
}
